/**
 * HeaderSearch — Global search input + results dropdown
 * Extracted from Header.jsx for modularity.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useImpersonationNavigate } from '../../hooks/useImpersonationNavigate';
import { Search, User, Building, Calendar, MessageSquare } from 'lucide-react';
import { formatDate } from '../../utils/formatters';

export default function HeaderSearch({ token, businessId }) {
    const navigate = useImpersonationNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [loading, setLoading] = useState(false);
    const searchRef = useRef(null);
    const debounceTimer = useRef(null);

    const performSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) { setResults(null); setShowResults(false); return; }
        setLoading(true);
        try {
            const bizSuffix = businessId ? `&business_id=${businessId}` : '';
            const res = await fetch(`/api/search?q=${encodeURIComponent(q.trim())}${bizSuffix}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const hasResults = (data.businesses?.length > 0) || data.contacts.length > 0 || data.bookings.length > 0 || data.messages.length > 0;
                setResults(hasResults ? data : null);
                setShowResults(hasResults);
            }
        } catch {
            setResults(null);
        } finally {
            setLoading(false);
        }
    }, [token, businessId]);

    const handleChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        clearTimeout(debounceTimer.current);
        if (value.trim().length < 2) { setResults(null); setShowResults(false); return; }
        debounceTimer.current = setTimeout(() => performSearch(value), 300);
    };

    useEffect(() => {
        const handleClick = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const goTo = (path) => { setShowResults(false); setQuery(''); setResults(null); navigate(path); };

    const totalResults = results
        ? (results.businesses?.length || 0) + results.contacts.length + results.bookings.length + results.messages.length
        : 0;

    const SECTIONS = [
        { key: 'businesses', icon: Building, color: 'var(--accent-primary)', label: 'Businesses', getPath: (b) => `/admin/businesses/${b.id}`, getName: (b) => b.name, getSub: (b) => `${b.owner_name || ''}${b.owner_email ? ` · ${b.owner_email}` : ''}${b.phone ? ` · ${b.phone}` : ''}` },
        { key: 'contacts', icon: User, color: 'var(--accent-primary)', label: 'Contacts', getPath: (c) => `/contacts?id=${c.id}`, getName: (c) => c.name, getSub: (c) => c.email || c.phone || '' },
        { key: 'bookings', icon: Calendar, color: 'var(--warning)', label: 'Bookings', getPath: () => '/calendar', getName: (b) => b.contact_name || 'Booking', getSub: (b) => `${b.datetime ? formatDate(b.datetime) : ''} · ${b.status}`, border: true },
        { key: 'messages', icon: MessageSquare, color: 'var(--info)', label: 'Messages', getPath: () => '/inbox', getName: (m) => m.contact_name || m.contact_phone || 'Message', getSub: (m) => m.content?.substring(0, 60), border: true },
    ];

    return (
        <div className="search-bar hide-mobile" ref={searchRef} style={{ position: 'relative' }}>
            <Search size={18} className="search-icon" />
            <input
                type="text"
                placeholder="Search contacts, bookings..."
                className="search-input"
                value={query}
                onChange={handleChange}
                onFocus={() => { if (results) setShowResults(true); }}
            />
            {loading && (
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>...</div>
            )}

            {showResults && results && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    marginTop: '4px', background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-lg)', zIndex: 200,
                    boxShadow: 'var(--shadow-lg)', maxHeight: '360px',
                    overflowY: 'auto', minWidth: '320px'
                }}>
                    <div style={{ padding: '8px 12px', fontSize: '0.7rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-primary)' }}>
                        {totalResults} result{totalResults !== 1 ? 's' : ''}
                    </div>

                    {SECTIONS.map(section => {
                        const items = results[section.key];
                        if (!items?.length) return null;
                        const Icon = section.icon;
                        return (
                            <div key={section.key}>
                                <div style={{ padding: '6px 12px', fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', ...(section.border ? { borderTop: '1px solid var(--border-primary)' } : {}) }}>
                                    {section.label}
                                </div>
                                {items.map((item, i) => (
                                    <button key={`${section.key}-${item.id || i}`} onClick={() => goTo(section.getPath(item))} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                        padding: '8px 12px', background: 'none', border: 'none',
                                        color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer', textAlign: 'left'
                                    }}>
                                        <Icon size={14} style={{ color: section.color, flexShrink: 0 }} />
                                        <div style={{ overflow: 'hidden' }}>
                                            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{section.getName(item)}</div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{section.getSub(item)}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
